import styled, { css } from 'styled-components'
import { grid, GridProps, margin, MarginProps } from 'styled-system'
import themeGet from '@styled-system/theme-get'

import { DIV_SHOULD_FORWARD_PROP } from 'Config/shouldForwardProp'

import { LANDING_CONTENT_WIDTH_PX } from 'Constants/Landing'
import { MEMBERSHIP_PACKAGE_KIND } from 'Constants/MembershipPackages'

type VariantProps = {
  secondary?: boolean
  tertiary?: boolean
}

type DividerProps = MarginProps &
  VariantProps & {
    transparent?: boolean
  }

type CardProps = MarginProps & VariantProps

type PriceProps = MarginProps & VariantProps

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  max-width: ${LANDING_CONTENT_WIDTH_PX}px;
`

const shouldForwardProp = (prop: string) =>
  ![
    ...DIV_SHOULD_FORWARD_PROP,
    'kind',
    'secondary',
    'tertiary',
    'transparent',
  ].includes(prop)

export const GridContainer = styled.div.withConfig({
  shouldForwardProp,
})<GridProps>`
  display: grid;
  width: 100%;
  align-items: flex-end;

  ${grid}
`

const cardSecondaryCss = ({ secondary }: CardProps) =>
  secondary &&
  css`
    background-color: ${themeGet('colors.background.screenAlt')};
  `

const cardTertiaryCss = ({ tertiary }: CardProps) =>
  tertiary &&
  css`
    background-color: ${themeGet('colors.outerSpaceCrayola')};
    color: ${themeGet('colors.text.inverse')};
  `

export const Card = styled.div.withConfig({
  shouldForwardProp,
})<CardProps>`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 104px;
  padding-top: ${themeGet('space.5')}px;
  padding-bottom: ${themeGet('space.9')}px;
  padding-left: ${themeGet('space.5')}px;
  padding-right: ${themeGet('space.5')}px;
  background-color: ${themeGet('colors.white')};
  border-radius: 20px;
  color: ${themeGet('colors.text.primary')};

  button {
    position: absolute;
    bottom: 0;
    transform: translateY(50%);
  }

  ${cardSecondaryCss}
  ${cardTertiaryCss}

  ${margin}
`

const dividerTransparentCss = ({ transparent }: DividerProps) =>
  transparent &&
  css`
    background-color: transparent;
  `

const dividerSecondaryCss = ({ secondary }: DividerProps) =>
  secondary &&
  css`
    background-color: ${themeGet('colors.line.tertiary')};
  `

export const Divider = styled.div.withConfig({
  shouldForwardProp,
})<DividerProps>`
  display: flex;
  width: 100%;
  height: 1px;
  flex-shrink: 0;
  background-color: ${themeGet('colors.line.quaternary')}80;

  ${margin}

  ${dividerSecondaryCss}
  ${dividerTransparentCss}
`

type CellProps = {
  kind?: string
}

const cellKindCss = ({ kind }: CellProps) => {
  if (kind === MEMBERSHIP_PACKAGE_KIND.CLUB_PLATINUM) {
    return css`
      > * {
        color: ${themeGet('colors.secondary.main')};
      }
    `
  }

  if (kind === MEMBERSHIP_PACKAGE_KIND.BLACK_PARTNER) {
    return css`
      color: ${themeGet('colors.text.inverse')};
      background-color: ${themeGet('colors.background.screenContrast')};

      > * {
        color: ${themeGet('colors.primary.main')};
      }
    `
  }

  if (kind === MEMBERSHIP_PACKAGE_KIND.DIAMOND_PARTNER) {
    return css`
      color: ${themeGet('colors.text.secondary')};
      background-color: ${themeGet('colors.background.paperElevation0')};

      > * {
        color: ${themeGet('colors.text.secondary')};
      }
    `
  }

  return {}
}

export const Cell = styled.div.withConfig({
  shouldForwardProp,
})<CellProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  background-color: transparent;
  color: ${themeGet('colors.text.body')};

  > * {
    color: ${themeGet('colors.primary.main')};
  }

  ${cellKindCss}
`

const priceSecondaryCss = ({ secondary }: VariantProps) =>
  secondary &&
  css`
    background-color: ${themeGet('colors.secondary.main')};
  `

const priceTertiaryCss = ({ tertiary }: VariantProps) =>
  tertiary &&
  css`
    background-color: ${themeGet('colors.background.screenContrast')};
  `

export const Price = styled.div.withConfig({
  shouldForwardProp,
})<PriceProps>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 78px;
  background-color: ${themeGet('colors.primary.main')};
  gap: ${themeGet('space.1')}px;

  ${priceSecondaryCss}
  ${priceTertiaryCss}

  ${margin}
`
